import React, { FC, useEffect, useState } from 'react';
import { useMediaPredicate } from 'react-media-hook';
import { Dropdown, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { useHistory } from 'react-router';
import { KanbanBoard } from 'common/components/KanbanBoard';
import { IKanbanBoardDataSourceItem } from 'common/models/commonModel';
import { MEDIA_MOBILE_768 } from 'common/const/common.const';
import { CaretDown, Check } from 'common/svg';
import { ERoutesFranchiseePrivate, RoutesCirPrefix } from 'common/models/routesModel';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  getPurchaseOrderModelBySonum,
  getPurchaseOrderRecoStoreCollection,
} from 'app/store/reducers/purchaseOrder.reducer';
import {
  EPurchaseOrderStatus,
  EPurchaseOrderStatusFilterKey,
  EPurchaseOrderStatusFilterLabel,
  IPurchaseOrderRecoStoreModel,
} from 'entities/PurchaseOrder/PurchaseOrder.models';
import { PurchaseOrderModal } from 'entities/PurchaseOrder/components/PurchaseOrderModal';
import { mapTrackPoKanbanDataSource, TrackPoKanbanItem } from 'entities/PurchaseOrder/PurchaseOrder.helper';
import { TrackPOKanbanCard } from 'entities/PurchaseOrder/components/TrackPOKanbanCard';
import { TrackPORecoCard } from 'entities/PurchaseOrder/components/TrackPORecoCard';
import { PurchaseOrderCard } from 'entities/PurchaseOrder/components/PurchaseOrderCard';
import { updateStorageStoreCode } from 'entities/Store/Store.helper';

export const TrackPOKanbanBoard: FC = () => {
  const dispatch = useAppDispatch();

  const { purchaseOrderCollection, purchaseOrderModel, purchaseOrderRecoStoreCollection } = useAppSelector(
    (state) => state.purchaseOrder
  );
  const { authUser } = useAppSelector((state) => state.auth);

  const [dataSource, setDataSource] = useState<IKanbanBoardDataSourceItem<TrackPoKanbanItem>[]>([]);
  const [openPurchaseOrderModal, setOpenPurchaseOrderModal] = useState<boolean>(false);
  const [openPurchaseOrderCard, setOpenPurchaseOrderCard] = useState<boolean>(false);
  const [openStatusFilter, setOpenStatusFilter] = useState<boolean>(false);
  const [status, setStatus] = useState<EPurchaseOrderStatusFilterKey>(EPurchaseOrderStatusFilterKey.ForApproval);
  const history = useHistory();
  const isMobile: boolean = useMediaPredicate(MEDIA_MOBILE_768);

  const orders = purchaseOrderCollection?.data || [];
  const recoStores = purchaseOrderRecoStoreCollection?.data || [];

  // MBC-85: Track PO RECO column data source.
  useEffect(() => {
    dispatch(getPurchaseOrderRecoStoreCollection());
  }, []);

  useEffect(() => {
    if (purchaseOrderCollection) {
      const dataSource = mapTrackPoKanbanDataSource(orders, recoStores);

      setDataSource(dataSource);
    }
  }, [purchaseOrderCollection, purchaseOrderRecoStoreCollection]);

  // MBC-85: navigate to Generate PO with the store pre-selected via router state.
  const goToGeneratePoWithStore = (item: IPurchaseOrderRecoStoreModel) => {
    updateStorageStoreCode(item.storeCode.toString());
    history.push(`${RoutesCirPrefix}${ERoutesFranchiseePrivate.GeneratePO}`);
  };

  const menuItems = dataSource.map((item) => {
    return {
      label: item.title,
      key: item.key,
    };
  });
  // MBC-85: mobile filter — RECO mode shows reco-stores, others show statused orders.
  const filteredOrders: TrackPoKanbanItem[] = (() => {
    if (status === EPurchaseOrderStatusFilterKey.Reco) {
      return recoStores;
    }
    if (status === EPurchaseOrderStatusFilterKey.DeliveredCanceled) {
      return orders.filter(
        (item) => item.status === EPurchaseOrderStatus.Delivered || item.status === EPurchaseOrderStatus.Canceled
      );
    }
    return orders.filter((item) => item.status === status);
  })();

  const openOrderModal = async (sonum: string) => {
    await dispatch(getPurchaseOrderModelBySonum({ sonum }));
    setOpenPurchaseOrderModal(true);
  };

  const closeOrderModal = () => {
    setOpenPurchaseOrderModal(false);
  };

  const openOrderCard = async (sonum: string) => {
    await dispatch(getPurchaseOrderModelBySonum({ sonum }));
    setOpenPurchaseOrderCard(true);
  };

  const closeOrderCard = () => {
    setOpenPurchaseOrderCard(false);
  };

  const changeStatusFilter: MenuProps['onClick'] = ({ key }) => {
    setStatus(key as EPurchaseOrderStatusFilterKey);
  };

  return (
    <>
      {!isMobile ? (
        <KanbanBoard<TrackPoKanbanItem>
          dataSource={dataSource}
          renderCard={(item, index) => {
            // MBC-85: discriminate by 'sonum' — IPurchaseOrder vs IPurchaseOrderRecoStoreModel.
            if ('sonum' in item) {
              return <TrackPOKanbanCard key={index} item={item} onView={() => openOrderModal(item.sonum)} />;
            }
            return <TrackPORecoCard key={index} item={item} onClick={goToGeneratePoWithStore} />;
          }}
        />
      ) : (
        <div>
          <Dropdown
            rootClassName="purchase-order-status-filter-mob__dropdown"
            open={openStatusFilter}
            onOpenChange={setOpenStatusFilter}
            placement="bottomRight"
            trigger={['click']}
            dropdownRender={() => {
              return (
                <Menu selectedKeys={[status]} onClick={changeStatusFilter}>
                  {menuItems.map((item) => {
                    const checked = item.key === status;
                    return (
                      <>
                        <Menu.Item key={item.key}>
                          {item.label}
                          {checked ? <Check className="icon-light-purple" /> : null}
                        </Menu.Item>
                        <Menu.Divider />
                      </>
                    );
                  })}
                </Menu>
              );
            }}
          >
            <div className="purchase-order-status-filter-mob">
              <div className="flex flex-align-center">
                <span className="ff-medium fs-xs">{EPurchaseOrderStatusFilterLabel[status]}</span>
                <div className="ml-3 purchase-order-status-filter-mob__order-count">{filteredOrders.length}</div>
              </div>
              {openStatusFilter ? (
                <CaretDown className="icon-gray" style={{ transform: 'rotate(180deg)' }} />
              ) : (
                <CaretDown className="icon-gray" />
              )}
            </div>
          </Dropdown>

          <div className="mt-5 mb-5">
            {filteredOrders.map((item, index) => {
              // MBC-85: same discriminator on mobile.
              if ('sonum' in item) {
                return <TrackPOKanbanCard key={index} item={item} onView={() => openOrderCard(item.sonum)} />;
              }
              return <TrackPORecoCard key={index} item={item} onClick={goToGeneratePoWithStore} />;
            })}
          </div>
        </div>
      )}

      <PurchaseOrderModal
        open={openPurchaseOrderModal}
        order={purchaseOrderModel}
        franchisee={authUser}
        onCancel={closeOrderModal}
      />

      <PurchaseOrderCard
        open={openPurchaseOrderCard}
        order={purchaseOrderModel}
        franchisee={authUser}
        onCancel={closeOrderCard}
      />
    </>
  );
};
